import { useState, useEffect, useCallback } from 'react';
import api from '../api';
import ConfirmDialog from '../components/ConfirmDialog';
import { SearchIcon, PlusIcon, EyeIcon, EditIcon, TrashIcon, XIcon, BuildingIcon, UsersIcon, WalletIcon, MapPinIcon, PhoneIcon, MailIcon, ChevronLeftIcon, ChevronRightIcon, ShieldIcon, FileIcon } from '../components/Icons';

const EMPTY = { name: '', contactPerson: '', contactNo: '', email: '', location: '', crNumber: '', vatNumber: '', status: 'Active', otMultiplier: 1.5, notes: '' };

const inputCls = "w-full px-3.5 py-2.5 border border-slate-200 rounded-xl text-[13.5px] text-slate-700 bg-white outline-none focus:border-[#fb866d] transition-all placeholder:text-slate-400";
const labelCls = "block text-[11px] font-bold text-slate-400 uppercase tracking-wider mb-1.5";

function ClientModal({ initial, saving, error, onSave, onClose, isEdit }) {
  const [form, setForm] = useState({ ...initial });
  const set = e => setForm(p => ({ ...p, [e.target.name]: e.target.value }));

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4" style={{ background: 'rgba(15,27,51,0.5)', backdropFilter: 'blur(4px)' }}
      onClick={e => e.target === e.currentTarget && onClose()}>
      <div className="bg-white rounded-2xl w-[520px] max-w-full max-h-[90vh] flex flex-col shadow-2xl">
        <div className="flex items-center gap-3 px-6 py-5 border-b border-slate-100 flex-shrink-0">
          <div className="w-10 h-10 rounded-xl bg-[#0f1b33] flex items-center justify-center">
            <BuildingIcon size={18} className="text-white" />
          </div>
          <h2 className="text-base font-bold text-slate-800 flex-1">{isEdit ? 'Edit Client' : 'New Client / Project'}</h2>
          <button onClick={onClose} className="w-8 h-8 rounded-lg flex items-center justify-center text-slate-400 hover:bg-slate-100 transition-all">
            <XIcon size={16} />
          </button>
        </div>
        <div className="overflow-y-auto flex-1 p-6 space-y-4">
          <div>
            <label className={labelCls}>Client / Project Name *</label>
            <input className={inputCls} name="name" placeholder="e.g. Movenpick" value={form.name} onChange={set} />
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className={labelCls}>Contact Person</label>
              <input className={inputCls} name="contactPerson" placeholder="Name" value={form.contactPerson} onChange={set} />
            </div>
            <div>
              <label className={labelCls}>Contact No.</label>
              <input className={inputCls} name="contactNo" placeholder="05XXXXXXXX" value={form.contactNo} onChange={set} />
            </div>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className={labelCls}>Email</label>
              <input className={inputCls} type="email" name="email" placeholder="contact@client.com" value={form.email} onChange={set} />
            </div>
            <div>
              <label className={labelCls}>Location</label>
              <input className={inputCls} name="location" placeholder="e.g. Jeddah" value={form.location} onChange={set} />
            </div>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className={labelCls}>CR Number</label>
              <input className={inputCls} name="crNumber" placeholder="e.g. 4030603769" value={form.crNumber || ''} onChange={set} />
            </div>
            <div>
              <label className={labelCls}>VAT Number</label>
              <input className={inputCls} name="vatNumber" placeholder="15-digit VAT no." value={form.vatNumber || ''} onChange={set} />
            </div>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className={labelCls}>Status</label>
              <select className={inputCls} name="status" value={form.status} onChange={set}>
                <option>Active</option><option>Inactive</option>
              </select>
            </div>
            <div>
              <label className={labelCls}>OT Multiplier</label>
              <input className={inputCls} type="number" step="0.25" min="0" name="otMultiplier"
                placeholder="1.5" value={form.otMultiplier ?? 1.5} onChange={set} />
              <p className="text-[10.5px] text-slate-400 mt-1">1.5 = standard KSA · 1 = flat rate (e.g. Ejar)</p>
            </div>
          </div>
          <div>
            <label className={labelCls}>Notes</label>
            <input className={inputCls} name="notes" placeholder="Any notes..." value={form.notes} onChange={set} />
          </div>
          {error && (
            <div className="px-4 py-3 rounded-xl text-sm bg-red-50 border border-red-200 text-red-600">{error}</div>
          )}
        </div>
        <div className="flex items-center justify-end gap-2.5 px-6 py-4 border-t border-slate-100 flex-shrink-0">
          <button onClick={onClose} className="px-5 py-2.5 border border-slate-200 rounded-full text-sm font-semibold text-slate-600 hover:border-slate-400 transition-all">Cancel</button>
          <button onClick={() => onSave(form)} disabled={saving || !form.name}
            className="px-6 py-2.5 rounded-full text-white text-sm font-semibold bg-[#fb866d] hover:bg-[#e8614a] transition-all disabled:opacity-60 disabled:cursor-not-allowed">
            {saving ? 'Saving...' : isEdit ? 'Save Changes' : 'Create Client'}
          </button>
        </div>
      </div>
    </div>
  );
}

function ClientDetail({ client, onClose }) {
  const [full, setFull] = useState(null);
  useEffect(() => {
    api.get(`/clients/${client.id}`).then(r => setFull(r.data));
  }, [client.id]);

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4" style={{ background: 'rgba(15,27,51,0.5)', backdropFilter: 'blur(4px)' }}
      onClick={e => e.target === e.currentTarget && onClose()}>
      <div className="bg-white rounded-2xl w-[640px] max-w-full max-h-[90vh] flex flex-col shadow-2xl">
        <div className="flex items-center gap-3 px-6 py-5 border-b border-slate-100 flex-shrink-0">
          <div className="w-10 h-10 rounded-xl bg-[#0f1b33] flex items-center justify-center">
            <BuildingIcon size={18} className="text-white" />
          </div>
          <div className="flex-1">
            <h2 className="text-base font-bold text-slate-800">{client.name}</h2>
            <p className="text-xs text-slate-400">{client.location || 'No location'}</p>
          </div>
          <button onClick={onClose} className="w-8 h-8 rounded-lg flex items-center justify-center text-slate-400 hover:bg-slate-100 transition-all">
            <XIcon size={16} />
          </button>
        </div>
        <div className="overflow-y-auto flex-1 p-6">
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-6">
            <div className="rounded-xl border border-slate-100 p-4 bg-slate-50/50">
              <UsersIcon size={16} className="text-[#fb866d] mb-2" />
              <p className="text-2xl font-bold text-slate-800">{client.headcount}</p>
              <p className="text-xs text-slate-400 mt-0.5">Active Employees</p>
            </div>
            <div className="rounded-xl border border-slate-100 p-4 bg-slate-50/50">
              <WalletIcon size={16} className="text-[#fb866d] mb-2" />
              <p className="text-2xl font-bold text-slate-800">{(client.monthlyPayroll/1000).toFixed(1)}K</p>
              <p className="text-xs text-slate-400 mt-0.5">Monthly Payroll (SAR)</p>
            </div>
            <div className="rounded-xl border border-slate-100 p-4 bg-slate-50/50">
              <BuildingIcon size={16} className="text-[#fb866d] mb-2" />
              <p className="text-lg font-bold text-slate-800">{client.status}</p>
              <p className="text-xs text-slate-400 mt-0.5">Status</p>
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-6">
            <div className="flex items-center gap-2.5 text-[13px] text-slate-600">
              <PhoneIcon size={14} className="text-slate-400" /> {client.contactNo || '—'}
            </div>
            <div className="flex items-center gap-2.5 text-[13px] text-slate-600">
              <MailIcon size={14} className="text-slate-400" /> {client.email || '—'}
            </div>
            <div className="flex items-center gap-2.5 text-[13px] text-slate-600">
              <UsersIcon size={14} className="text-slate-400" /> {client.contactPerson || 'No contact person'}
            </div>
            <div className="flex items-center gap-2.5 text-[13px] text-slate-600">
              <MapPinIcon size={14} className="text-slate-400" /> {client.location || '—'}
            </div>
            <div className="flex items-center gap-2.5 text-[13px] text-slate-600">
              <FileIcon size={14} className="text-slate-400" /> CR: {client.crNumber || '—'}
            </div>
            <div className="flex items-center gap-2.5 text-[13px] text-slate-600">
              <FileIcon size={14} className="text-slate-400" /> VAT: {client.vatNumber || '—'}
            </div>
          </div>

          <p className="text-[11px] font-bold text-slate-400 uppercase tracking-wider mb-3">Assigned Employees</p>
          {!full ? (
            <div className="text-center py-6 text-slate-400 text-sm">Loading...</div>
          ) : full.employees.length === 0 ? (
            <div className="text-center py-6 text-slate-400 text-sm">No employees assigned</div>
          ) : (
            <div className="border border-slate-100 rounded-xl overflow-hidden">
              {full.employees.map(e => (
                <div key={e.id} className="flex items-center justify-between px-4 py-2.5 border-b border-slate-50 last:border-0 text-[13px]">
                  <span className="font-medium text-slate-700">{e.name}</span>
                  <span className="text-slate-400">{e.designation || '—'}</span>
                  <span className={`px-2.5 py-0.5 rounded-full text-[11px] font-semibold ${e.status === 'Active' ? 'bg-emerald-50 text-emerald-600' : 'bg-red-50 text-red-500'}`}>
                    {e.status}
                  </span>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function PortalModal({ client, onClose }) {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [saving, setSaving] = useState(false);
  const [done, setDone] = useState(false);
  const [error, setError] = useState('');

  async function create() {
    setSaving(true); setError('');
    try {
      await api.post(`/clients/${client.id}/portal-user`, { email, password });
      setDone(true);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed');
    } finally { setSaving(false); }
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4" style={{ background: 'rgba(15,27,51,0.5)', backdropFilter: 'blur(4px)' }}
      onClick={e => e.target === e.currentTarget && onClose()}>
      <div className="bg-white rounded-2xl w-[440px] max-w-full shadow-2xl">
        <div className="flex items-center gap-3 px-6 py-5 border-b border-slate-100">
          <div className="w-10 h-10 rounded-xl bg-[#0f1b33] flex items-center justify-center">
            <ShieldIcon size={18} className="text-white" />
          </div>
          <div className="flex-1">
            <h2 className="text-base font-bold text-slate-800">Portal Access</h2>
            <p className="text-xs text-slate-400">{client.name}</p>
          </div>
          <button onClick={onClose} className="w-8 h-8 rounded-lg flex items-center justify-center text-slate-400 hover:bg-slate-100 transition-all">
            <XIcon size={16} />
          </button>
        </div>
        <div className="p-6 space-y-4">
          {done ? (
            <div className="px-4 py-4 rounded-xl bg-emerald-50 border border-emerald-200 text-[13.5px] text-emerald-800">
              <b>Login ready.</b> Share these with {client.name}:<br />
              Email: <b>{email}</b><br />Password: <b>{password}</b><br />
              <span className="text-xs opacity-80">They will only see their own attendance sheet — no salaries, no other clients.</span>
            </div>
          ) : (
            <>
              <p className="text-[13px] text-slate-500">
                Creates a login this client uses to mark daily attendance for <b>{client.name}</b> only.
                Re-submitting with the same email resets the password.
              </p>
              <div>
                <label className={labelCls}>Login Email</label>
                <input className={inputCls} type="email" placeholder={`attendance@${client.name.toLowerCase().replace(/[^a-z0-9]+/g,'')}.com`}
                  value={email} onChange={e => setEmail(e.target.value)} />
              </div>
              <div>
                <label className={labelCls}>Password</label>
                <input className={inputCls} placeholder="Set a password" value={password} onChange={e => setPassword(e.target.value)} />
              </div>
              {error && <div className="px-4 py-3 rounded-xl text-sm bg-red-50 border border-red-200 text-red-600">{error}</div>}
            </>
          )}
        </div>
        <div className="flex items-center justify-end gap-2.5 px-6 py-4 border-t border-slate-100">
          <button onClick={onClose} className="px-5 py-2.5 border border-slate-200 rounded-full text-sm font-semibold text-slate-600 hover:border-slate-400 transition-all">
            {done ? 'Close' : 'Cancel'}
          </button>
          {!done && (
            <button onClick={create} disabled={saving || !email || !password}
              className="px-6 py-2.5 rounded-full text-white text-sm font-semibold bg-[#fb866d] hover:bg-[#e8614a] transition-all disabled:opacity-60 disabled:cursor-not-allowed">
              {saving ? 'Creating...' : 'Create Login'}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

export default function Clients() {
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [editClient, setEditClient] = useState(null);
  const [detailClient, setDetailClient] = useState(null);
  const [portalClient, setPortalClient] = useState(null);
  const [deleteClient, setDeleteClient] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [deleteError, setDeleteError] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [page, setPage] = useState(1);
  const LIMIT = 10;

  const load = useCallback(() => {
    setLoading(true);
    api.get('/clients').then(r => setClients(r.data)).finally(() => setLoading(false));
  }, []);

  useEffect(() => { load(); }, [load]);

  const filtered = clients.filter(c => {
    const q = search.toLowerCase();
    return !q || c.name.toLowerCase().includes(q) || (c.location || '').toLowerCase().includes(q) || (c.contactPerson || '').toLowerCase().includes(q);
  });

  const totalPages = Math.max(1, Math.ceil(filtered.length / LIMIT));
  const paged = filtered.slice((page - 1) * LIMIT, page * LIMIT);
  const pages = [];
  const ps = Math.max(1, page - 2), pe = Math.min(totalPages, ps + 4);
  for (let i = ps; i <= pe; i++) pages.push(i);

  async function handleSave(form) {
    setSaving(true); setError('');
    try {
      if (editClient) await api.put(`/clients/${editClient.id}`, form);
      else await api.post('/clients', form);
      setShowModal(false); setEditClient(null); load();
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to save');
    } finally { setSaving(false); }
  }

  async function handleDelete() {
    setDeleting(true); setDeleteError('');
    try {
      await api.delete(`/clients/${deleteClient.id}`);
      setDeleteClient(null);
      load();
    } catch (err) {
      setDeleteError(err.response?.data?.error || 'Failed to delete');
    } finally { setDeleting(false); }
  }

  const totalHeadcount = clients.reduce((s, c) => s + c.headcount, 0);
  const totalPayroll = clients.reduce((s, c) => s + c.monthlyPayroll, 0);

  return (
    <div>
      {/* Topbar */}
      <div className="hidden lg:flex bg-white border-b border-slate-100 px-8 py-4 items-center justify-between sticky top-0 z-40">
        <h1 className="text-lg font-bold text-slate-800">Clients & Projects</h1>
        <span className="px-3 py-1.5 bg-slate-50 border border-slate-200 rounded-full text-xs font-semibold text-slate-600">Admin</span>
      </div>

      <div className="p-4 lg:p-8 stagger">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 mb-5">
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 rounded-xl bg-[#0f1b33] flex items-center justify-center">
              <BuildingIcon size={22} className="text-white" />
            </div>
            <div>
              <h2 className="text-[17px] font-bold text-slate-800">Client Sites</h2>
              <p className="text-[13px] text-slate-400">{clients.length} clients · {totalHeadcount} deployed workers · SAR {(totalPayroll/1000).toFixed(0)}K monthly payroll</p>
            </div>
          </div>
          <button onClick={() => { setEditClient(null); setError(''); setShowModal(true); }}
            className="flex items-center gap-2 px-5 py-2.5 rounded-full text-white text-[13px] font-semibold bg-[#fb866d] hover:bg-[#e8614a] transition-all shadow-sm">
            <PlusIcon size={15} /> New Client
          </button>
        </div>

        {/* Search */}
        <div className="bg-white rounded-2xl border border-slate-100 p-4 mb-4" style={{ boxShadow: '0 1px 3px rgba(15,27,51,0.06)' }}>
          <div className="relative">
            <SearchIcon size={15} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-slate-400" />
            <input
              className="w-full pl-10 pr-4 py-2.5 border border-slate-200 rounded-xl text-[13.5px] outline-none bg-white text-slate-700 focus:border-[#fb866d] transition-all placeholder:text-slate-400"
              placeholder="Search clients by name, location, contact..."
              value={search} onChange={e => { setSearch(e.target.value); setPage(1); }}
            />
          </div>
        </div>

        {/* Table */}
        <div className="bg-white rounded-2xl border border-slate-100 overflow-hidden" style={{ boxShadow: '0 1px 3px rgba(15,27,51,0.06)' }}>
          <div className="overflow-x-auto">
            <table className="w-full" style={{ minWidth: 800 }}>
              <thead>
                <tr className="bg-slate-50/70">
                  {['Client / Project','Location','Contact Person','Headcount','Monthly Payroll','OT ×','Status',''].map(h => (
                    <th key={h} className="px-5 py-3.5 text-left text-[11px] font-bold text-slate-400 uppercase tracking-wider border-b border-slate-100 whitespace-nowrap">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr><td colSpan={8} className="text-center py-12 text-slate-400 text-sm">
                    <div className="inline-flex items-center gap-2.5">
                      <div className="w-4 h-4 border-2 border-slate-200 border-t-[#fb866d] rounded-full animate-spin" />
                      Loading...
                    </div>
                  </td></tr>
                ) : filtered.length === 0 ? (
                  <tr><td colSpan={8} className="text-center py-16">
                    <div className="text-[15px] font-semibold text-slate-500">No clients found</div>
                  </td></tr>
                ) : paged.map(c => (
                  <tr key={c.id} className="border-b border-slate-50 odd:bg-slate-50/50 hover:bg-[#fde8e3]/40 transition-colors last:border-0">
                    <td className="px-5 py-3.5">
                      <div className="flex items-center gap-3">
                        <div className="w-9 h-9 rounded-lg bg-[#fde8e3] flex items-center justify-center flex-shrink-0">
                          <BuildingIcon size={15} className="text-[#e8614a]" />
                        </div>
                        <span className="text-[13.5px] font-semibold text-slate-800">{c.name}</span>
                      </div>
                    </td>
                    <td className="px-5 py-3.5 text-[13px] text-slate-600">{c.location || '—'}</td>
                    <td className="px-5 py-3.5 text-[13px] text-slate-600">{c.contactPerson || '—'}</td>
                    <td className="px-5 py-3.5">
                      <span className="inline-flex items-center gap-1.5 text-[13px] font-semibold text-slate-700">
                        <UsersIcon size={13} className="text-slate-400" /> {c.headcount}
                      </span>
                    </td>
                    <td className="px-5 py-3.5 text-[13.5px] font-semibold text-slate-800">SAR {(c.monthlyPayroll || 0).toLocaleString()}</td>
                    <td className="px-5 py-3.5 text-[13px] text-slate-600">×{c.otMultiplier ?? 1.5}</td>
                    <td className="px-5 py-3.5">
                      <span className={`inline-flex px-3 py-1 rounded-full text-[11.5px] font-semibold border ${
                        c.status === 'Active' ? 'bg-emerald-50 text-emerald-600 border-emerald-200' : 'bg-slate-50 text-slate-500 border-slate-200'
                      }`}>{c.status}</span>
                    </td>
                    <td className="px-5 py-3.5">
                      <div className="flex gap-1.5">
                        <button onClick={() => setDetailClient(c)} title="View"
                          className="w-8 h-8 flex items-center justify-center rounded-lg border border-slate-200 text-slate-500 hover:border-[#fb866d] hover:text-[#e8614a] hover:bg-[#fde8e3] transition-all">
                          <EyeIcon size={14} />
                        </button>
                        <button onClick={() => { setEditClient(c); setError(''); setShowModal(true); }} title="Edit"
                          className="w-8 h-8 flex items-center justify-center rounded-lg border border-slate-200 text-slate-500 hover:border-[#fb866d] hover:text-[#e8614a] hover:bg-[#fde8e3] transition-all">
                          <EditIcon size={14} />
                        </button>
                        <button onClick={() => setPortalClient(c)} title="Portal access"
                          className="w-8 h-8 flex items-center justify-center rounded-lg border border-slate-200 text-slate-500 hover:border-[#0f1b33] hover:text-[#0f1b33] hover:bg-slate-50 transition-all">
                          <ShieldIcon size={14} />
                        </button>
                        <button onClick={() => { setDeleteError(''); setDeleteClient(c); }} title="Delete"
                          className="w-8 h-8 flex items-center justify-center rounded-lg border border-slate-200 text-slate-500 hover:border-red-400 hover:text-red-500 hover:bg-red-50 transition-all">
                          <TrashIcon size={14} />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {filtered.length > 0 && (
            <div className="flex items-center justify-between px-6 py-3.5 border-t border-slate-100">
              <span className="text-[12.5px] text-slate-400">
                Showing {(page-1)*LIMIT+1}–{Math.min(page*LIMIT, filtered.length)} of {filtered.length}
              </span>
              <div className="flex gap-1.5">
                <button onClick={() => setPage(p => Math.max(1,p-1))} disabled={page===1}
                  className="w-8 h-8 rounded-lg border border-slate-200 flex items-center justify-center text-slate-500 hover:border-[#fb866d] hover:text-[#e8614a] disabled:opacity-40 disabled:cursor-not-allowed transition-all">
                  <ChevronLeftIcon size={14} />
                </button>
                {pages.map(p => (
                  <button key={p} onClick={() => setPage(p)}
                    className={`w-8 h-8 rounded-lg border text-[13px] font-semibold transition-all ${
                      page === p ? 'bg-[#fb866d] border-[#fb866d] text-white' : 'border-slate-200 text-slate-600 hover:border-[#fb866d] hover:text-[#e8614a]'
                    }`}>
                    {p}
                  </button>
                ))}
                <button onClick={() => setPage(p => Math.min(totalPages,p+1))} disabled={page===totalPages}
                  className="w-8 h-8 rounded-lg border border-slate-200 flex items-center justify-center text-slate-500 hover:border-[#fb866d] hover:text-[#e8614a] disabled:opacity-40 disabled:cursor-not-allowed transition-all">
                  <ChevronRightIcon size={14} />
                </button>
              </div>
            </div>
          )}
        </div>
      </div>

      {showModal && (
        <ClientModal
          initial={editClient || EMPTY}
          saving={saving}
          error={error}
          onSave={handleSave}
          onClose={() => { setShowModal(false); setEditClient(null); }}
          isEdit={!!editClient}
        />
      )}
      {detailClient && <ClientDetail client={detailClient} onClose={() => setDetailClient(null)} />}
      {portalClient && <PortalModal client={portalClient} onClose={() => setPortalClient(null)} />}

      <ConfirmDialog
        open={!!deleteClient}
        tone="danger"
        title={`Delete client "${deleteClient?.name}"?`}
        message="This removes the client record. Deletion is blocked if active employees are still assigned to it."
        confirmLabel="Delete Client"
        busy={deleting}
        error={deleteError}
        onConfirm={handleDelete}
        onClose={() => !deleting && setDeleteClient(null)}
      />
    </div>
  );
}